import * as React from "react";
import { convertProgramNameString } from "helpers/dataConversion/convertProgramNameString";
import { formatClassAgeRange } from "helpers/dataConversion/convertClassAgeRange";
import { convertDaysOffered } from "helpers/dataConversion/convertDaysOffered";
import { convertTimeOffered } from "helpers/dataConversion/convertTimeOffered";
import { convertURLEncoded } from "helpers/dataConversion/convertURLEncoded";
import { getScheduleFilterAnalyticsParams } from "helpers/analytics/scheduleFilterAnalyticsParams";
import { isClassScheduleFilterActive } from "helpers/jackrabbit/classScheduleFilter";
import { getClassUrgency } from "helpers/jackrabbit/classUrgency";
import "./programsTable.css";

const buildContactMailto = (row) => {
  const className = convertProgramNameString(row.name);
  const days = convertDaysOffered(row.meeting_days);
  const time = convertTimeOffered(
    row.start_time,
    row.end_time,
    row.meeting_days
  );
  return `mailto:office@ace-gymnastics.com?subject=Inquiry about ${className} class&body=Hello,%0D%0A%0D%0AI'm interested in the ${className} class.%0D%0A%0D%0AClass Details:%0D%0A- Class: ${className}%0D%0A- Days: ${days}%0D%0A- Time: ${time}%0D%0A- Tuition: $${row.tuition.fee}%0D%0A%0D%0APlease let me know about enrollment options for this class.%0D%0A%0D%0AThank you!`;
};

const getFilterTrackAttributes = (scheduleFilter) => {
  if (!isClassScheduleFilterActive(scheduleFilter)) return {};

  const params = getScheduleFilterAnalyticsParams(scheduleFilter);
  return {
    "data-track-filter-active": "true",
    "data-track-filter-days": params.filter_days,
    "data-track-filter-time": params.filter_time,
    "data-track-filter-age-years": params.filter_age_years,
    "data-track-filter-age-months": params.filter_age_months,
    "data-track-filter-open-spots-only": params.filter_open_spots_only,
  };
};

const ClassUrgencyNote = ({ urgency }) => {
  if (!urgency?.message) return null;

  if (urgency.level === "high") {
    return (
      <p className="class-schedule__urgency-text" aria-live="polite">
        <strong>{urgency.message}</strong>
        {urgency.actionHint ? (
          <span className="class-schedule__urgency-text-detail">
            {" "}
            — {urgency.actionHint}
          </span>
        ) : null}
      </p>
    );
  }

  return (
    <div className="class-schedule__urgency-wrap">
      <span
        className={`class-schedule__urgency-pill class-schedule__urgency-pill--${urgency.level}`}
      >
        {urgency.message}
      </span>
      {urgency.actionHint ? (
        <p className="class-schedule__urgency-hint">{urgency.actionHint}</p>
      ) : null}
    </div>
  );
};

const ClassActionCell = ({ row, scheduleFilter }) => {
  const className = convertProgramNameString(row.name);
  const filterTrackAttributes = getFilterTrackAttributes(scheduleFilter);
  const urgency = getClassUrgency(row.openings);

  if (row.online_reg_link) {
    const hasOpenings = urgency.spots > 0;
    const href = convertURLEncoded(row.online_reg_link);
    const label = hasOpenings ? urgency.registerLabel : "Waitlist";
    const showButtonPulse =
      hasOpenings && (urgency.level === "high" || urgency.level === "medium");
    const actionClassName = [
      "class-schedule__action",
      hasOpenings
        ? "class-schedule__action--register"
        : "class-schedule__action--waitlist",
    ].join(" ");

    const registerButton = (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={actionClassName}
        data-track="program_class_click"
        data-track-location={window.location.pathname}
        data-track-item={className}
        data-track-label={label}
        data-track-spots-remaining={String(urgency.spots)}
        data-track-urgency-level={urgency.level}
        {...filterTrackAttributes}
      >
        {label}
      </a>
    );

    return (
      <div className="class-schedule__enroll-cell">
        {showButtonPulse ? (
          <span
            className={`class-schedule__action-pulse${
              urgency.level === "high"
                ? " class-schedule__action-pulse--high"
                : ""
            }`}
          >
            {registerButton}
          </span>
        ) : (
          registerButton
        )}
        <ClassUrgencyNote urgency={urgency} />
      </div>
    );
  }

  return (
    <a
      href={buildContactMailto(row)}
      className="class-schedule__action class-schedule__action--contact"
      data-track="email_click"
      data-track-location="program_schedule"
      data-track-item={className}
      {...filterTrackAttributes}
    >
      Contact
    </a>
  );
};

export default function CustomizedTables(props) {
  return (
    <div className="class-schedule">
      <table className="class-schedule__table" aria-label="Class schedule">
        <thead>
          <tr>
            <th scope="col">Enroll</th>
            <th scope="col">Class</th>
            <th scope="col">Ages</th>
            <th scope="col">Days</th>
            <th scope="col">Times</th>
            <th scope="col">Tuition (4 weeks)</th>
          </tr>
        </thead>
        <tbody>
          {props.rows.map((row, key) => {
            const urgency = getClassUrgency(row.openings);

            return (
              <tr key={row.id ?? key} className={urgency.rowClassName || undefined}>
                <td>
                  <ClassActionCell
                    row={row}
                    scheduleFilter={props.scheduleFilter}
                  />
                </td>
                <td className="class-schedule__class-name">
                  {convertProgramNameString(row.name)}
                </td>
                <td className="class-schedule__ages">
                  {formatClassAgeRange(row.min_age, row.max_age) || "—"}
                </td>
                <td>{convertDaysOffered(row.meeting_days)}</td>
                <td>
                  {convertTimeOffered(
                    row.start_time,
                    row.end_time,
                    row.meeting_days
                  )}
                </td>
                <td className="class-schedule__tuition">${row.tuition.fee}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
